import { motion } from "framer-motion";
import { Star, ShieldCheck, Building2, MapPin } from "lucide-react";
import { COMPANY, IMAGES } from "@/data/content";
import QuoteForm from "./QuoteForm";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.1 * i, duration: 0.6, ease: "easeOut" } }),
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Roof replacement on a Pacific Northwest home in Snohomish County, WA" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-2 gap-12 items-center">
        {/* Left copy */}
        <div>
          <motion.div custom={0} variants={fade} initial="hidden" animate="show"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-honey/30 bg-honey/10 mb-6">
            <MapPin className="w-3.5 h-3.5 text-honey" />
            <span className="text-xs font-semibold tracking-wide text-honey">Snohomish • King • Skagit County • Everett, WA</span>
          </motion.div>

          <motion.h1 custom={1} variants={fade} initial="hidden" animate="show"
            className="font-display font-black uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            Snohomish County's <span className="text-honey">Honest</span> Roofing Co.
          </motion.h1>

          <motion.p custom={2} variants={fade} initial="hidden" animate="show"
            className="text-lg text-zinc-300 mt-6 max-w-xl leading-relaxed">
            Roof replacement, storm &amp; wind damage, decks, siding and exteriors across Snohomish, King &amp; Skagit County.
            Based in Everett, WA — flat-rate pricing and a <span className="text-white font-semibold">50-year CertainTeed guarantee</span>.
          </motion.p>

          <motion.div custom={3} variants={fade} initial="hidden" animate="show"
            className="flex flex-wrap gap-x-6 gap-y-3 mt-7">
            {[
              { icon: Star, text: "5.0 Rated Local Crew" },
              { icon: ShieldCheck, text: "Licensed & Insured WA" },
              { icon: Building2, text: "Commercial & Residential" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-2 text-sm text-zinc-200">
                <b.icon className="w-4 h-4 text-honey" /> {b.text}
              </div>
            ))}
          </motion.div>

          <motion.div custom={4} variants={fade} initial="hidden" animate="show" className="mt-8 flex items-center gap-4">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="hero-call-btn"
              className="border border-white/20 text-white font-semibold px-6 py-3.5 rounded-sm hover:border-honey hover:text-honey transition-colors">
              Call {COMPANY.phone}
            </a>
          </motion.div>
        </div>

        {/* Right form card */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
          className="bg-carbon/80 backdrop-blur-xl border border-white/10 rounded-md p-6 sm:p-8 shadow-2xl">
          <div className="mb-5">
            <h2 className="text-2xl font-display font-bold text-white">Get a Free Roof Evaluation</h2>
            <p className="text-sm text-zinc-400 mt-1">Fixed, no-hassle pricing — takes 30 seconds.</p>
          </div>
          <QuoteForm />
        </motion.div>
      </div>
    </section>
  );
}
